import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

import stringify from '../utils/stableStringify';
import { unpack, pack } from 'msgpackr';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import { Template } from 'devextreme-react/core/template';
import AntLocalization from '../utils/AntLocalization';

import AntArray from '../utils/AntArray';

import { AntGlobal, AntSession } from '../utils/AntContext';
import AntAPI from '../utils/AntAPI';
import { readGridSettings, updateGridSettings, takeLegacyCategoryExpanded } from '../utils/AntGridSettings';

// Số giá trị hiện của mỗi nhóm do AntGridCategoryLayout (AntGrid truyền layoutCoordinator)
// phân bổ theo độ rộng thật. Dùng lẻ (không có bộ điều phối) thì thu gọn khi quá mức này.
// Ghi đè theo từng nhóm bằng searchCategories.maxVisibleItems (0 = không thu gọn).
const DEFAULT_MAX_VISIBLE_ITEMS = 8;
// Nút "..." / thu gọn là item cuối của chính ButtonGroup để nằm cuối dòng cuối, không rớt xuống dòng mới.
const TOGGLE_ITEM_VALUE = '__ant_grid_categories_toggle__';
const isToggleItem = (item) => Boolean(item && item.isToggle);

// Chữ hiển thị của một giá trị lọc (dùng chung cho nút và cho bộ điều phối đo độ rộng).
const getItemText = (item) => {
    const iText =
        typeof item.rawValue === 'object' && item.rawValue
            ? item.rawValue['value'] !== undefined
                ? item.rawValue['value']
                : item.rawValue['text'] !== undefined
                ? item.rawValue['text']
                : item.rawValue['name'] !== undefined
                ? item.rawValue['name']
                : item.rawValue['code']
            : item.rawValue;
    return AntLocalization.translateText(iText);
};

const writeGridExpanded = (gridSettingsKey, meta, key, expanded) =>
    updateGridSettings(
        gridSettingsKey,
        (entry) => {
            const categoriesExpanded = { ...(entry.categoriesExpanded || {}) };
            if (expanded) categoriesExpanded[key] = true;
            else delete categoriesExpanded[key];
            return { ...entry, categoriesExpanded };
        },
        meta
    );

const hasItemText = (text) => text !== null && text !== undefined && String(text).trim() !== '';

export default forwardRef(function (props, ref) {
    const appGlobal = AntGlobal();
    const API = useMemo(() => (props.API ? props.API : props.module && props.table ? new AntAPI(props.module, props.table) : undefined), []);

    const forceRender = useCallback(() => {
        setShouldRender((v) => v + 1);
    }, []);

    const [shouldRender, setShouldRender] = useState(1);

    const isRender = useMemo(() => shouldRender + (props.shouldRender ? props.shouldRender : 1), [props.shouldRender, shouldRender]);

    const [data, setData] = useState([]);

    const refCats = useRef();
    const lastRefreshVersion = useRef(0);

    const maxVisibleItems =
        props.searchCategories && props.searchCategories.maxVisibleItems !== undefined
            ? Number(props.searchCategories.maxVisibleItems) || 0
            : DEFAULT_MAX_VISIBLE_ITEMS;

    // Khoá ghi nhớ thu gọn/mở rộng cho đúng nhóm lọc: bảng + danh sách lookups của nhóm.
    const expandedStateKey = useMemo(() => {
        const lookups = props.searchCategories && props.searchCategories.lookups instanceof Array ? props.searchCategories.lookups : [];
        const lookupKeys = lookups.map((l) => (typeof l === 'object' && l ? l.field || l.name : l)).join(',');
        return (API && API.routeURL ? API.routeURL : '') + '|' + lookupKeys;
    }, [API, props.searchCategories]);

    // Lưu ở categoriesExpanded của cấu hình lưới chứa nhóm lọc (AntGridSettings). Không có khoá → chỉ nhớ trong phiên.
    const gridSettingsKey = props.gridSettingsKey;
    const readExpanded = useCallback(() => {
        if (!gridSettingsKey) return false;
        const saved = readGridSettings(gridSettingsKey).categoriesExpanded;
        if (saved && saved[expandedStateKey] !== undefined) return Boolean(saved[expandedStateKey]);
        const legacy = takeLegacyCategoryExpanded(expandedStateKey);
        if (legacy) writeGridExpanded(gridSettingsKey, props.gridSettingsMeta, expandedStateKey, true);
        return Boolean(legacy);
    }, [gridSettingsKey, expandedStateKey]);
    const writeExpanded = useCallback(
        (next) => {
            if (gridSettingsKey) writeGridExpanded(gridSettingsKey, props.gridSettingsMeta, expandedStateKey, next);
        },
        [gridSettingsKey, expandedStateKey, props.gridSettingsMeta]
    );

    const [expanded, setExpanded] = useState(readExpanded);

    useEffect(() => {
        setExpanded(readExpanded());
    }, [readExpanded]);

    const explicitMaxVisible =
        props.searchCategories && props.searchCategories.maxVisibleItems !== undefined
            ? Number(props.searchCategories.maxVisibleItems) || 0
            : undefined;

    // { quota, collapsible } do bộ điều phối cấp; null = chưa tính xong / không có bộ điều phối.
    const [layout, setLayout] = useState(null);

    const toggleExpanded = useCallback(() => {
        setExpanded((current) => {
            const next = !current;
            writeExpanded(next);
            return next;
        });
    }, [writeExpanded]);

    const onSelected = useCallback(
        (addedItems, removedItems, allItems) => {
            if (!addedItems && !removedItems) return;

            let ar = props.selectedItems instanceof Array ? JSON.parse(stringify(props.selectedItems)) : [];
            if (removedItems instanceof Array && removedItems.length > 0) {
                for (let i = 0; i < removedItems.length; i++) {
                    const removedItem = removedItems[i];
                    ar = ar.filter((s) => !(s.name === removedItem.name && s.value === removedItem.value));
                }
            }

            if (addedItems instanceof Array && addedItems.length > 0) {
                for (let i = 0; i < addedItems.length; i++) {
                    const addedItem = addedItems[i];

                    if (props.searchCategories.lookups.every((s) => s.name !== addedItem.name)) continue;

                    let existed = props.selectedItems.find((s) => s.name === addedItem.name && s.value === addedItem.value);
                    if (existed) {
                        ar = ar.filter((s) => !(s.name === addedItem.name && s.value === addedItem.value));
                    } else {
                        if (!props.searchCategories.multiSelect) ar = ar.filter((s) => s.name !== addedItem.name);
                        ar.push({
                            field: addedItem.field,
                            name: addedItem.name,
                            value: addedItem.value,
                            rawValue: addedItem.rawValue
                        });
                    }
                }
            }

            ar = JSON.parse(stringify(ar));

            if (props.onSelectionChanged) props.onSelectionChanged({ selectedItems: ar, allItems: allItems });
        },
        [props.selectedItems, props.searchCategories, props.onSelectionChanged]
    );

    const loadData = useCallback(
        async (oldData) => {
            if (props.allowQuery && !props.allowQuery()) return;

            let objSearch = {
                ...props.searchConditions,
                searchText: props.searchText,
                ...props.searchCategories.searchConditions
            };

            if (props.searchCategories && props.searchCategories.lookups instanceof Array)
                props.searchCategories.lookups.forEach((k) => delete objSearch[k.field]);

            delete objSearch.checkActions;

            if (objSearch.searchText === '' || objSearch.searchText === 'undefined') delete objSearch.searchText;

            const staticValues = props.searchCategories.values instanceof Array ? props.searchCategories.values : null;
            let result = null;
            if (!staticValues) {
                try {
                    const explicitRefresh = props.searchCategories.cachePolicy === 'ar-orders-catalogues-v1' &&
                        props.refreshVersion > lastRefreshVersion.current;
                    if (explicitRefresh) lastRefreshVersion.current = props.refreshVersion;
                    result = await API.postSearchPrepare(objSearch, {
                        selectLookups: props.searchCategories.lookups.map((c) => {
                            if (typeof c === 'object' && c.name) return c.name;
                            return c;
                        }),
                        caching: props.searchCategories.caching,
                        cachePolicy: props.searchCategories.cachePolicy,
                        freshness: explicitRefresh ? 'realtime' : props.searchCategories.freshness
                    });
                } catch (error) {
                    // An offline category request must not erase the values that
                    // were already rendered from a local product snapshot.
                    return;
                }
            }
            if (!staticValues && !result) return;

            let ar = staticValues
                ? staticValues.map((value) => {
                      const lookup = props.searchCategories.lookups[0] || {};
                      return {
                          name: lookup.name,
                          rawValue: value?.name || value?.text || value,
                          value: value?.code !== undefined ? value.code : value?.value !== undefined ? value.value : value,
                          field: lookup.field || lookup.name
                      };
                  })
                : result && result.lookups
                    ? result.lookups
                          .map((l) => {
                              return l.values.map((v) => {
                                  let found = props.searchCategories.lookups.find(
                                      (c) => c === l.name || (typeof c === 'object' && c && c.name === l.name)
                                  );

                                  return {
                                      name: l.name,
                                      rawValue: v,
                                      value:
                                          typeof v === 'object' && v
                                              ? v['code'] !== undefined
                                                  ? v['code']
                                                  : v['value'] !== undefined
                                                  ? v['value']
                                                  : v
                                              : v,
                                      field: found && typeof found === 'object' && found.field ? found.field : l.name
                                  };
                              });
                          })
                          .flat()
                    : [];

            if (props.selectedItems instanceof Array && props.selectedItems.length > 0) {
                for (let i = 0; i < props.selectedItems.length; i++) {
                    const sItem = props.selectedItems[i];
                    if (sItem.value) {
                        if (!ar.some((a) => a.name === sItem.name && a.value === sItem.value)) {
                            ar.push(sItem);
                        }
                    }
                }
            }

            if (props.searchCategories.excludes instanceof Array) ar = ar.filter((a) => props.searchCategories.excludes.includes(a.value) === false);

            ar = ar.map((a) => {
                return {
                    name: a.name,
                    rawValue: a.rawValue,
                    value: a.value,
                    field: a.field
                };
            });

            //             let str1 = stringify(ar.map((a) => a.value + a.name));
            //             let str2 = stringify(oldData.map((a) => a.value + a.name));
            // ;

            let isChanged = stringify(ar.map((a) => a.value + a.name)) !== stringify(oldData.map((a) => a.value + a.name));

            if (isChanged) {
                // Object.assign(oldData.current, JSON.parse(stringify(ar)));
                setData(JSON.parse(stringify(ar)));

                if (props.onLoadedData) props.onLoadedData({ allItems: ar });
                if (props.autoSelectFirst && ar.length > 0) {
                    setTimeout(
                        (o) => {
                            onSelected(o);
                        },
                        500,
                        ar[0]
                    );
                }
            }
        },
        [
            isRender,
            props.allowQuery,
            props.refreshVersion,
            props.searchConditions,
            props.searchText,
            props.searchCategories,
            props.selectedItems,
            props.onLoadedData,
            props.autoSelectFirst,
            API,
            onSelected
        ]
    );

    const initMainItems = useCallback(
        (oldData) => {
            var items = [];

            let selectedKeys =
                props.selectedItems instanceof Array
                    ? props.selectedItems
                          .map((g) =>
                              typeof g.value === 'object' && g.value
                                  ? g.value['code'] !== undefined
                                      ? g.value['code']
                                      : g.value['value'] !== undefined
                                      ? g.value['value']
                                      : g.value
                                  : g.value
                          )
                          .flat()
                    : [];

            if (oldData instanceof Array) {
                for (let i = 0; i < oldData.length; i++) {
                    oldData[i].text = getItemText(oldData[i]);
                }
                let ar = oldData.filter((btn) => hasItemText(btn.text));

                const keyExpr = props.keyExpr ? props.keyExpr : 'value';
                const useLayout = Boolean(layout) && explicitMaxVisible === undefined;
                const quota = useLayout ? layout.quota : maxVisibleItems;
                const canCollapse = useLayout
                    ? expanded
                        ? layout.collapsible
                        : quota < ar.length
                    : maxVisibleItems > 0 && ar.length > maxVisibleItems;
                // Thu gọn vẫn giữ các giá trị đang chọn để người dùng thấy bộ lọc đang áp dụng.
                const visibleItems =
                    canCollapse && !expanded
                        ? ar.filter((btn, index) => index < quota || selectedKeys.includes(btn[keyExpr]))
                        : ar;

                const groupItems = canCollapse
                    ? [
                          ...visibleItems,
                          {
                              [keyExpr]: TOGGLE_ITEM_VALUE,
                              isToggle: true,
                              expanded: expanded,
                              text: expanded ? '' : '...',
                              hint: expanded
                                  ? AntLocalization.translateText({ vi: 'Thu gọn', en: 'Show less', fr: 'Réduire', es: 'Mostrar menos', id: 'Ciutkan', ja: '折りたたむ', zh: '收起', ko: '접기', hi: 'कम दिखाएँ', ar: 'عرض أقل' })
                                  : AntLocalization.translateText({ vi: 'Xem thêm', en: 'Show more', fr: 'Voir plus', es: 'Ver más', id: 'Lihat lainnya', ja: 'もっと見る', zh: '显示更多', ko: '더 보기', hi: 'और देखें', ar: 'عرض المزيد' }),
                              elementAttr: { class: 'ant-grid-categories-toggle' }
                          }
                      ]
                    : visibleItems;

                if (visibleItems.length > 0)
                    items.push({
                        location: 'before',
                        locateInMenu: 'never',
                        widget: 'dxButtonGroup',
                        options: {
                            items: groupItems,
                            selectedItemKeys: selectedKeys instanceof Array ? selectedKeys : [],
                            keyExpr: keyExpr,
                            // selectionMode: props.searchCategories && Boolean(props.searchCategories.multiSelect) ? 'multiple' : 'single',
                            selectionMode: 'multiple',
                            focusStateEnabled: false,
                            icon: props.icon,
                            text: props.text,
                            width: props.width,
                            buttonTemplate: 'renderButtonOnGroup',
                            stylingMode: props.stylingMode ? props.stylingMode : 'text',
                            onSelectionChanged: (e) => {
                                const added = e.addedItems instanceof Array ? e.addedItems : [];
                                const removed = e.removedItems instanceof Array ? e.removedItems : [];
                                // Bấm nút "..." / thu gọn: chỉ đổi trạng thái hiển thị, không phải chọn giá trị lọc.
                                // Chỉ xét addedItems: lúc dựng lại items, nút bị bỏ chọn theo chương trình sẽ nằm ở removedItems.
                                if (added.some(isToggleItem)) toggleExpanded();
                                const addedValues = added.filter((item) => !isToggleItem(item));
                                const removedValues = removed.filter((item) => !isToggleItem(item));
                                if (addedValues.length > 0 || removedValues.length > 0) onSelected(addedValues, removedValues, oldData);
                            }
                        }
                    });
            }

            return items;
        },
        [
            props.selectedItems,
            props.searchCategories,
            props.keyExpr,
            props.icon,
            props.text,
            props.width,
            props.stylingMode,
            onSelected,
            maxVisibleItems,
            explicitMaxVisible,
            layout,
            expanded,
            toggleExpanded
        ]
    );

    // Báo cho bộ điều phối danh sách giá trị (đúng thứ tự hiển thị), giá trị đang chọn và
    // trạng thái mở rộng để phân bổ số giá trị hiện theo độ rộng khung.
    const coordinator = props.layoutCoordinator;
    const layoutKey = props.layoutKey;
    useEffect(() => {
        if (!coordinator || layoutKey === undefined) return undefined;
        const keyExpr = props.keyExpr ? props.keyExpr : 'value';
        const selectedValues =
            props.selectedItems instanceof Array
                ? props.selectedItems.map((g) =>
                      typeof g.value === 'object' && g.value
                          ? g.value['code'] !== undefined
                              ? g.value['code']
                              : g.value['value'] !== undefined
                              ? g.value['value']
                              : g.value
                          : g.value
                  )
                : [];
        const shown = (data instanceof Array ? data : [])
            .map((btn) => ({ btn, text: getItemText(btn) }))
            .filter((entry) => hasItemText(entry.text));
        coordinator.register(
            layoutKey,
            {
                texts: shown.map((entry) => entry.text),
                selected: shown.map((entry) => selectedValues.includes(entry.btn[keyExpr])),
                expanded: expanded,
                visible: props.visible !== false,
                fixedQuota: explicitMaxVisible === undefined ? undefined : explicitMaxVisible > 0 ? explicitMaxVisible : shown.length
            },
            setLayout
        );
    }, [coordinator, layoutKey, data, props.selectedItems, props.keyExpr, props.visible, expanded, explicitMaxVisible]);

    useEffect(() => {
        if (!coordinator || layoutKey === undefined) return undefined;
        return () => coordinator.unregister(layoutKey);
    }, [coordinator, layoutKey]);

    
    const renderButtonOnGroup = useCallback((btn) => {
        if (isToggleItem(btn)) {
            return (
                <div style={{ verticalAlign: 'center', display: 'inline-flex' }}>
                    {btn.expanded ? (
                        <i className="dx-icon dx-icon-chevronleft" style={{ color: 'var(--AnteTitleColor)' }} />
                    ) : (
                        <span className="dx-button-text ant-grid-categories-toggle-text">...</span>
                    )}
                </div>
            );
        }

        return (
            <div
                style={{
                    verticalAlign: 'center',
                    display: 'inline-flex'
                }}
            >
                <span
                    className="dx-button-text"
                    style={{
                        // fontWeight: 'bold',
                        color: 'var(--AnteTitleColor)',
                        fontSize: '12px',
                        fontWeight: '500',
                        textTransform: 'uppercase'
                    }}
                >
                    {btn.text}
                </span>
            </div>
        );
    }, []);

    const [mainItems, setMainItems] = useState(initMainItems(data));

    useEffect(() => {
        // loadData([]);

        return () => {
            delete refCats.current;
            refCats.current = null;
        };
    }, []);

    useEffect(() => {
        loadData(data);
    }, [loadData]);

    useEffect(() => {
        setMainItems(initMainItems(data));
    }, [data, isRender, initMainItems]);

    return (
        <React.Fragment>
            {/* {mainItems?.length > 0 && ( */}
            <Toolbar
                className={props.withoutBorder ? 'ant-grid-categories ant-grid-categories-none-border' : 'ant-grid-categories'}
                ref={refCats}
                items={mainItems}
                focusStateEnabled={props.focusStateEnabled}
                disabled={props.disabled}
                elementAttr={{
                    // style: props.selectedItems.length > 0 || (data.length > 1 && props.visible !== false) ? 'display:block' : 'display:none'
                    // style: props.selectedItems.length > 0 || props.visible !== false ? 'display:block' : 'display:none'
                    style: props.visible !== false ? 'display:block' : 'display:none'
                    // style:  'display:block'
                    // style: {
                    //     paddingBottom: '0px !important',
                    //     paddingTop: '0px !important',
                    //     display: mainItems.length > 1 ? 'block !important' : 'none !important'
                    // }
                }}
                multiline={true}
            >
                <Template name={'renderButtonOnGroup'} render={renderButtonOnGroup} />
            </Toolbar>
            {/* )} */}
        </React.Fragment>
    );
});
